import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { BriefcaseBusiness, CalendarDays, Clock, Coins, Layers, MapPin } from "lucide-react";
import { Badge } from "../../../../../../../components/ui/badge";
import { useCompanyCurrency, useCompanyTimeFormat } from "../../../../../../../utils/hooks/company";
import { useOrgSettingsResourceLabel } from "../../../../../../../utils/hooks/useOrgSettingsResourceLabel";
import { useGroups } from "../../../../../../Settings/General/OrganisationLevelGroups/context";
import { renderUserName } from "../../../../../../../utils/helpers/user";
import { renderSettingsResourceLabel } from "../../../../../../../utils/helpers/settings";
import { renderCurrencySymbol } from "../../../../../../../utils/helpers/job";
import { getShiftInfo } from "./helpers";
import { QUALIFICATIONS } from "./constants";

function DetailRow({ icon: Icon, label, children, className }: {
    icon: any;
    label: ReactNode;
    children: ReactNode;
    className?: string;
}) {
    return (
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-1">
            <div className="tw-flex tw-items-center tw-gap-3">
                <Icon size={19} className="tw-text-primary" />
                <span className="tw-text-sm">{label}</span>
            </div>
            <div className={className}>{children}</div>
        </div>
    );
}

function ShiftDetails({ request }: { request: any }) {
    const { t } = useTranslation();
    const currency = useCompanyCurrency();
    const timeFormat = useCompanyTimeFormat();
    const { allocatedGroups } = useGroups();
    const renderOrgLevelResourceLabel = useOrgSettingsResourceLabel();

    const { section, date, startDate, endDate, job, location } = getShiftInfo(request);
    const extraPayment = request?.data?.extraPayment;
    const hasExtraPay = extraPayment?.payAmount != null && extraPayment?.payAmount !== "";
    const createdBy = request?.createdBy;

    return (
        <div>
            <div className="tw-flex tw-items-center tw-gap-3 tw-mb-1">
                <CalendarDays size={19} className="tw-text-primary" />
                <span className="tw-text-sm">{moment(date).format("ddd, DD MMMM YYYY")}</span>
            </div>

            {startDate && endDate && (
                <div className="tw-flex tw-items-center tw-gap-3 tw-mb-1">
                    <Clock size={19} className="tw-text-primary" />
                    <span className="tw-text-sm">
                        {moment.parseZone(startDate).format(timeFormat)}
                        {" - "}
                        {moment.parseZone(endDate).format(timeFormat)}
                    </span>
                </div>
            )}

            {job && (
                <DetailRow
                    icon={BriefcaseBusiness}
                    label={t("job")}
                    className="tw-bg-primary/20 tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-text-primary tw-font-semibold"
                >
                    {renderSettingsResourceLabel(job)}
                </DetailRow>
            )}

            {location && (
                <DetailRow
                    icon={MapPin}
                    label={t("location")}
                    className="tw-bg-purple-200 tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-text-purple-950 tw-font-semibold"
                >
                    {renderSettingsResourceLabel(location)}
                </DetailRow>
            )}

            {allocatedGroups?.map((group: any) => {
                const level = group.level.replaceAll("_", "");
                const value = section?.[level] || request?.data?.[level];
                return value && (
                    <DetailRow
                        key={group.id ?? level}
                        icon={Layers}
                        label={group.description}
                        className="tw-bg-sky-100 tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-text-sky-900 tw-font-semibold"
                    >
                        {renderOrgLevelResourceLabel(value)}
                    </DetailRow>
                );
            })}

            {hasExtraPay && (
                <DetailRow
                    icon={Coins}
                    label={t("extra-pay")}
                    className="tw-bg-green-100 tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-text-green-800 tw-font-semibold"
                >
                    {`${renderCurrencySymbol(currency)}${extraPayment.payAmount} `}
                    ({t(extraPayment.payType === "hourly" ? "per-hour" : "flat-amount").toLowerCase()})
                </DetailRow>
            )}

            {QUALIFICATIONS.map(({ key, icon: Icon }) => {
                const values: any[] = request?.data?.[key] || [];
                return values.length > 0 && (
                    <div key={key} className="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-mb-1">
                        <div className="tw-flex tw-items-center tw-gap-3 tw-shrink-0">
                            <Icon size={19} className="tw-text-primary" />
                            <span className="tw-text-sm">{t(key)}</span>
                        </div>
                        <div className="tw-flex tw-flex-wrap tw-justify-end tw-gap-1">
                            {values.map((value) => (
                                <Badge key={value?.id ?? value?.code} variant="outline" className="tw-whitespace-nowrap">
                                    {renderSettingsResourceLabel(value)}
                                </Badge>
                            ))}
                        </div>
                    </div>
                );
            })}

            {request?.type === "direct-offer" && createdBy && typeof createdBy === "object" && (
                <div className="tw-text-xs tw-text-neutral-500 tw-mt-2">
                    {t("offered-by-user", { user: renderUserName(createdBy) })}
                </div>
            )}

            {request?.data?.reason && (
                <div className="tw-text-sm tw-text-neutral-600 tw-whitespace-pre-wrap tw-mt-2">
                    {request.data.reason}
                </div>
            )}
        </div>
    );
}

export default ShiftDetails;
